// src/pages/ProjectIllustrations.jsx
import { useEffect, useRef, useState } from 'react';
import '../styles/ProjectPage.css';
import { illustrations } from '../data/illustrations';

const MOBILE_BREAKPOINT = '(max-width: 932px)';
const REPEAT = 3; // nombre de fois où chaque colonne est dupliquée, pour boucler sans coupure visible
const WHEEL_SPEED = 0.6; // sensibilité du défilement au scroll
const AUTO_SPEED = 0.3; // px par frame, défilement continu même sans scroll
const EASE = 0.06; // vitesse de rattrapage de l'inertie (plus petit = plus fluide/flottant)

function splitIntoColumns(images, count) {
    const columns = Array.from({ length: count }, () => []);
    images.forEach((img, i) => columns[i % count].push(img));
    return columns;
}

export default function ProjectIllustrations() {
    const columnRefs = useRef([]);
    const targetsRef = useRef([]); // cible brute, non bornée, poussée par le scroll/autoplay
    const positionsRef = useRef([]); // position affichée, rattrape la cible en douceur
    const [columnCount, setColumnCount] = useState(4);

    useEffect(() => {
        const mq = window.matchMedia(MOBILE_BREAKPOINT);
        const update = () => setColumnCount(mq.matches ? 2 : 4);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    // Page figée : le scroll classique est désactivé tant qu'on est sur cette vue,
    // c'est le wheel/touch qui pilote directement le défilement des colonnes.
    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        const prevBodyTouch = document.body.style.touchAction;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
        window.scrollTo(0, 0);
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
            document.body.style.touchAction = prevBodyTouch;
        };
    }, []);

    useEffect(() => {
        if (illustrations.length === 0) return;

        let rafId;

        // Pousse la cible de chaque colonne (accumulateur non borné, jamais affiché tel quel).
        const push = (deltaY) => {
            columnRefs.current.forEach((col, i) => {
                if (!col) return;
                targetsRef.current[i] = (targetsRef.current[i] || 0) + deltaY;
            });
        };

        // Colonnes paires (1, 3...) montent, colonnes impaires (2, 4...) descendent.
        const render = () => {
            columnRefs.current.forEach((col, i) => {
                if (!col) return;
                const H = col.scrollHeight / REPEAT;
                if (!H) return;

                const target = targetsRef.current[i] || 0;
                const current = positionsRef.current[i] || 0;
                const eased = current + (target - current) * EASE;
                positionsRef.current[i] = eased;

                const pos = ((eased % H) + H) % H;
                const goesUp = i % 2 === 0;
                col.style.transform = goesUp ? `translateY(${-pos}px)` : `translateY(${pos - H}px)`;
            });
        };

        const tick = () => {
            push(AUTO_SPEED);
            render();
            rafId = requestAnimationFrame(tick);
        };

        const onWheel = (e) => {
            e.preventDefault();
            push(e.deltaY * WHEEL_SPEED);
        };

        let touchStartY = null;
        const onTouchStart = (e) => {
            touchStartY = e.touches[0].clientY;
        };
        const onTouchMove = (e) => {
            if (touchStartY === null) return;
            const currentY = e.touches[0].clientY;
            const deltaY = touchStartY - currentY;
            touchStartY = currentY;
            e.preventDefault();
            push(deltaY * WHEEL_SPEED);
        };

        render(); // positionne correctement les colonnes descendantes dès le départ
        rafId = requestAnimationFrame(tick);

        window.addEventListener('wheel', onWheel, { passive: false });
        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: false });

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('wheel', onWheel);
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchmove', onTouchMove);
        };
    }, [columnCount]);

    columnRefs.current = [];
    const columns = splitIntoColumns(illustrations, columnCount);

    return (
        <div className="project-page illustrations-page illustrations-page--locked">
            {illustrations.length > 0 ? (
                <div
                    className="parallax-gallery parallax-gallery--full"
                    style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
                >
                    {columns.map((col, i) => (
                        <div className="parallax-column" key={i} ref={(el) => (columnRefs.current[i] = el)}>
                            {Array.from({ length: REPEAT }).flatMap((_, r) =>
                                col.map((img) => (
                                    <div className="illustration-frame" key={`${r}-${img.src}`}>
                                        <img src={img.src} alt={img.alt} />
                                    </div>
                                ))
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="tech-placeholder">
                    <p>Galerie en cours de préparation.</p>
                </div>
            )}
        </div>
    );
}
